import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Box, Collapse, Divider, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useState } from 'react';
import { Menu, useSidebarState } from 'react-admin';
import { isOwner } from './authProvider';

/**
 * The navigation, split by how often it is used.
 *
 * Orders and products are the day's work and sit at the top with nothing
 * between them. Everything else - the banners, the pages, the contacts, the
 * accounts and their groups - is occasional maintenance of the website, and
 * lives in a section of its own so it does not compete with the queue.
 *
 * Expanded by default, because a collapsed section is a section people forget
 * exists; the state is remembered once they close it.
 *
 * Administrator accounts get a section of their own below a second divider,
 * and only the owner sees it. It is deliberately not filed under "Web a účty"
 * with the customers: those are the shop's users, these are the people who can
 * change prices, and putting them in the same drawer invites the wrong click.
 */
const SECTION_KEY = 'menuSiteOpen';

export function AdminMenu() {
  const [sidebarOpen] = useSidebarState();
  const [open, setOpen] = useState(() => localStorage.getItem(SECTION_KEY) !== 'false');

  const toggle = () => {
    setOpen(current => {
      localStorage.setItem(SECTION_KEY, String(!current));
      return !current;
    });
  };

  return (
    <Menu>
      <Menu.DashboardItem primaryText="Prehľad" />

      {/* The work. */}
      <Menu.ResourceItem name="orders" />
      <Menu.ResourceItem name="products" />

      <Divider sx={{ my: 1 }} />

      {/* The website and the accounts behind it. */}
      <ListItemButton onClick={toggle} sx={{ pl: 2 }}>
        <ListItemIcon sx={{ minWidth: 40 }}><LanguageIcon /></ListItemIcon>
        {sidebarOpen && (
          <>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary">
                  Web a účty
                </Typography>
              }
            />
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </>
        )}
      </ListItemButton>

      {/* When the sidebar is collapsed to icons there is no header to click,
          so the section stays open rather than becoming unreachable. */}
      <Collapse in={open || !sidebarOpen} timeout="auto" unmountOnExit>
        <Box sx={{ pl: sidebarOpen ? 1 : 0 }}>
          <Menu.ResourceItem name="offers" />
          <Menu.ResourceItem name="content" />
          <Menu.ResourceItem name="staff" />
          <Menu.ResourceItem name="loyalty" />
          <Menu.ResourceItem name="users" />
          <Menu.ResourceItem name="groups" />
          <Menu.ResourceItem name="organizations" />
          <Menu.ResourceItem name="producers" />
        </Box>
      </Collapse>

      {/* Owner only. The API refuses these routes to everyone else regardless,
          so this hides a door that is already locked rather than being the
          lock itself. */}
      {isOwner() && (
        <>
          <Divider sx={{ my: 1 }} />
          {sidebarOpen && (
            <Box sx={{ pl: 2, pt: 0.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <AdminPanelSettingsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Správa administrácie
              </Typography>
            </Box>
          )}
          <Box sx={{ pl: sidebarOpen ? 1 : 0 }}>
            <Menu.ResourceItem name="admins" />
          </Box>
        </>
      )}
    </Menu>
  );
}
